import React, { useEffect } from 'react';
import usePageView from './hooks/usePageView.effect';
import { Placeholder, RichText, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import ConsentCookie from './components/@shared/consentCookie';
import LayoutSeoTags from './components/@shared/layoutSeoTags';
import JSONLDSeoTags from './components/@shared/JSONLDSeoTags';
import StickyTopWrapper from './components/@shared/stickyTopWrapper';
import ReactGA from 'react-ga4';
import { windowService, documentService } from './utils/window.utils';
import { googleDataLayerPush, getPreselectedMarketInDealerLocator } from './utils/analytics';
import { BRANDS, BRAND_CSS_CLASSES } from './constants/brandNames.contants';

const getBrandClassName = (sitecoreContext) => {
  if (sitecoreContext?.brand === BRANDS.NEW_HOLLAND) {
    return BRAND_CSS_CLASSES.NEW_HOLLAND;
  }

  return BRAND_CSS_CLASSES.CASE;
};

const Layout = React.memo(({ route, sitecoreContext }) => {
  const themeClassName = getBrandClassName(sitecoreContext);
  const matomoUrl = sitecoreContext?.analytics?.['matomoUrl'];
  const matomoId = sitecoreContext?.analytics?.['matomoId'];
  const matomoTagManagerContainer = sitecoreContext?.analytics?.['matomoTagManagerContainer'];
  const matomoTagManagerScript =
    matomoUrl && matomoTagManagerContainer
      ? `<script type="text/javascript">
          var _mtm = window._mtm = window._mtm || [];
          _mtm.push({ 'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start' });
          var d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
          g.type = 'text/javascript'; g.async = true; g.src = '${matomoUrl}js/container_${matomoTagManagerContainer}.js'; s.parentNode.insertBefore(g, s);
      </script>`
      : '';

  const googleAnalyticsId = sitecoreContext?.analytics?.['googleAnalyticsId'];
  const googleTagManagerIds = sitecoreContext?.analytics?.['googleTagManagerIds'];
  const googleTagManagerIdWithServerSideDomain = sitecoreContext?.analytics?.['googleTagManagerIdWithServerSideDomain'];

  const applicationInsightInstrumentationKey = sitecoreContext?.analytics?.['applicationInsight']?.['instrumentationKey'];
  const aiScript = applicationInsightInstrumentationKey
    ? `<script type="text/javascript">
    !function(T,l,y){var S=T.location,k="script",D="instrumentationKey",C="ingestionendpoint",I="disableExceptionTracking",E="ai.device.",b="toLowerCase",w="crossOrigin",N="POST",e="appInsightsSDK",t=y.name||"appInsights";(y.name||T[e])&&(T[e]=t);var n=T[t]||function(d){var g=!1,f=!1,m={initialize:!0,queue:[],sv:"5",version:2,config:d};function v(e,t){var n={},a="Browser";return n[E+"id"]=a[b](),n[E+"type"]=a,n["ai.operation.name"]=S&&S.pathname||"_unknown_",n["ai.internal.sdkVersion"]="javascript:snippet_"+(m.sv||m.version),{time:function(){var e=new Date;function t(e){var t=""+e;return 1===t.length&&(t="0"+t),t}return e.getUTCFullYear()+"-"+t(1+e.getUTCMonth())+"-"+t(e.getUTCDate())+"T"+t(e.getUTCHours())+":"+t(e.getUTCMinutes())+":"+t(e.getUTCSeconds())+"."+((e.getUTCMilliseconds()/1e3).toFixed(3)+"").slice(2,5)+"Z"}(),iKey:e,name:"Microsoft.ApplicationInsights."+e.replace(/-/g,"")+"."+t,sampleRate:100,tags:n,data:{baseData:{ver:2}}}}var h=d.url||y.src;if(h){function a(e){var t,n,a,i,r,o,s,c,u,p,l;g=!0,m.queue=[],f||(f=!0,t=h,s=function(){var e={},t=d.connectionString;if(t)for(var n=t.split(";"),a=0;a<n.length;a++){var i=n[a].split("=");2===i.length&&(e[i[0][b]()]=i[1])}if(!e[C]){var r=e.endpointsuffix,o=r?e.location:null;e[C]="https://"+(o?o+".":"")+"dc."+(r||"services.visualstudio.com")}return e}(),c=s[D]||d[D]||"",u=s[C],p=u?u+"/v2/track":d.endpointUrl,(l=[]).push((n="SDK LOAD Failure: Failed to load Application Insights SDK script (See stack for details)",a=t,i=p,(o=(r=v(c,"Exception")).data).baseType="ExceptionData",o.baseData.exceptions=[{typeName:"SDKLoadFailed",message:n.replace(/\./g,"-"),hasFullStack:!1,stack:n+"\\nSnippet failed to load ["+a+"] -- Telemetry is disabled\\nHelp Link: https://go.microsoft.com/fwlink/?linkid=2128109\\nHost: "+(S&&S.pathname||"_unknown_")+"\\nEndpoint: "+i,parsedStack:[]}],r)),l.push(function(e,t,n,a){var i=v(c,"Message"),r=i.data;r.baseType="MessageData";var o=r.baseData;return o.message='AI (Internal): 99 message:"'+("SDK LOAD Failure: Failed to load Application Insights SDK script (See stack for details) ("+n+")").replace(/\"/g,"")+'"',o.properties={endpoint:a},i}(0,0,t,p)),function(e,t){if(JSON){var n=T.fetch;if(n&&!y.useXhr)n(t,{method:N,body:JSON.stringify(e),mode:"cors"});else if(XMLHttpRequest){var a=new XMLHttpRequest;a.open(N,t),a.setRequestHeader("Content-type","application/json"),a.send(JSON.stringify(e))}}}(l,p))}function i(e,t){f||setTimeout(function(){!t&&m.core||a()},500)}var e=function(){var n=l.createElement(k);n.src=h;var e=y[w];return!e&&""!==e||"undefined"==n[w]||(n[w]=e),n.onload=i,n.onerror=a,n.onreadystatechange=function(e,t){"loaded"!==n.readyState&&"complete"!==n.readyState||i(0,t)},n}();y.ld<0?l.getElementsByTagName("head")[0].appendChild(e):setTimeout(function(){l.getElementsByTagName(k)[0].parentNode.appendChild(e)},y.ld||0)}try{m.cookie=l.cookie}catch(p){}function t(e){for(;e.length;)!function(t){m[t]=function(){var e=arguments;g||m.queue.push(function(){m[t].apply(m,e)})}}(e.pop())}var n="track",r="TrackPage",o="TrackEvent";t([n+"Event",n+"PageView",n+"Exception",n+"Trace",n+"DependencyData",n+"Metric",n+"PageViewPerformance","start"+r,"stop"+r,"start"+o,"stop"+o,"addTelemetryInitializer","setAuthenticatedUserContext","clearAuthenticatedUserContext","flush"]),m.SeverityLevel={Verbose:0,Information:1,Warning:2,Error:3,Critical:4};var s=(d.extensionConfig||{}).ApplicationInsightsAnalytics||{};if(!0!==d[I]&&!0!==s[I]){var c="onerror";t(["_"+c]);var u=T[c];T[c]=function(e,t,n,a,i){var r=u&&u(e,t,n,a,i);return!0!==r&&m["_"+c]({message:e,url:t,lineNumber:n,columnNumber:a,error:i}),r},d.autoExceptionInstrumented=!0}return m}(y.cfg);function a(){y.onInit&&y.onInit(n)}(T[t]=n).queue&&0===n.queue.length?(n.queue.push(a),n.trackPageView({})):a()}(window,document,{
    src: "https://js.monitor.azure.com/scripts/b/ai.2.min.js", // The SDK URL Source
    // name: "appInsights", // Global SDK Instance name defaults to "appInsights" when not supplied
    // ld: 0, // Defines the load delay (in ms) before attempting to load the sdk. -1 = block page load and add to head. (default) = 0ms load after timeout,
    // useXhr: 1, // Use XHR instead of fetch to report failures (if available),
    crossOrigin: "anonymous", // When supplied this will add the provided value as the cross origin attribute on the script tag
    // onInit: null, // Once the application insights instance has loaded and initialized this callback function will be called with 1 argument -- the sdk instance (DO NOT ADD anything to the sdk.queue -- as they won't get called)
    cfg: { // Application Insights Configuration
      instrumentationKey:"${applicationInsightInstrumentationKey}"
    }});
    </script>`
    : '';

  // Matomo
  useEffect(() => {
    if (matomoUrl && matomoId) {
      const _paq = (window._paq = windowService()?._paq || []);
      _paq.push(['enableLinkTracking']);
      (function () {
        _paq.push(['setTrackerUrl', matomoUrl + 'matomo.php']);
        _paq.push(['setSiteId', matomoId]);
        const d = documentService(),
          g = d.createElement('script'),
          s = d.getElementsByTagName('script')[0];
        g.type = 'text/javascript';
        g.async = true;
        g.src = matomoUrl + 'matomo.js';
        s.parentNode.insertBefore(g, s);
      })();
    }
  }, []); //eslint-disable-line

  // Matomo redirect
  usePageView(() => {
    if (matomoUrl && matomoTagManagerContainer) {
      const _mtm = (window._mtm = windowService()?._mtm || []);
      _mtm.push({ event: 'mtm.PageView' });
      _mtm.push({ event: 'WindowLoad' });
    }

    const _paq = (window._paq = windowService()?._paq || []);
    _paq.push(['setCustomUrl', windowService()?.location?.href]);
    _paq.push(['setDocumentTitle', documentService()?.title]);
    _paq.push(['deleteCustomVariables', 'page']);
    _paq.push(['trackPageView']);
  }, !(matomoUrl && matomoId));

  // Google Analytics 4
  useEffect(() => {
    if (googleAnalyticsId) {
      ReactGA.initialize(googleAnalyticsId);
    }
  }, []); /* eslint-disable-line */

  const addGTMScript = (gtmId, gtmUrl ) => {
    const url = gtmUrl || 'https://www.googletagmanager.com';
    const scriptText = `
      (function(w,d,s,l,i){w[l]=w[l]||[];
        w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js', });
        var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;j.src='${url}/gtm.js?id='+i+dl+'&gtm_auth=&gtm_preview=&gtm_cookies_win=x';
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
      `;
      const script = documentService()?.createElement("script");
      script.innerText = scriptText;
      // eslint-disable-next-line
      documentService()?.head?.prepend(script);

      const noScriptText = `
      <iframe src="${url}/ns.html?id=${gtmId}&gtm_auth=&gtm_preview=&gtm_cookies_win=x"
      height="0" width="0" style="display:none;visibility:hidden" id="tag-manager"></iframe>
      `;

      const noScript = documentService()?.createElement("noscript");
      noScript.innerText = noScriptText;
      // eslint-disable-next-line
      documentService()?.body?.prepend(noScript);
  }

  // Google Tag Manager
  useEffect(() => {
    const gtmIds = googleTagManagerIds || [];
    const gtmCustomDomains = googleTagManagerIdWithServerSideDomain || [];
    gtmIds.forEach((gtmId) => {
      addGTMScript(gtmId, '');
    });

    gtmCustomDomains.forEach((gtmElem) => {
      addGTMScript(gtmElem?.tag, gtmElem?.domain);
    });
  }, []); //eslint-disable-line

  useEffect(() => {
    const bodyElm = documentService()?.body;

    if (bodyElm?.classList && themeClassName) {
      if (bodyElm.classList.contains('casece')) {
        bodyElm.classList.remove('casece');
      } else if (bodyElm.classList.contains('newhollandce')) {
        bodyElm.classList.remove('newhollandce');
      }
      bodyElm.classList.add(themeClassName);
    }
  }, [themeClassName]);

  // Google DataLayer
  useEffect(() => {
    const dataLayerData = {
      event: 'page_load',
      region: sitecoreContext?.analytics?.['region'],
      language: sitecoreContext?.['language'],
      market: getPreselectedMarketInDealerLocator(sitecoreContext) ?? sitecoreContext?.analytics?.['market'],
      site_section: sitecoreContext?.analytics?.['siteSection'] ? sitecoreContext?.analytics?.['siteSection'] : 'Homepage',
      site_subsection: sitecoreContext?.analytics?.['siteSubsection'] ? sitecoreContext?.analytics?.['siteSubsection'] : '',
      product_line: sitecoreContext?.analytics?.['productLine'] ? sitecoreContext?.analytics?.['productLine'] : '',
      product_series: sitecoreContext?.analytics?.['productSeries'] ? sitecoreContext?.analytics?.['productSeries'] : '',
      product_model: sitecoreContext?.analytics?.['productModel'] ? sitecoreContext?.analytics?.['productModel'] : '',
    };
    googleDataLayerPush(dataLayerData);
  }, [sitecoreContext?.analytics]);

  return (
    <React.Fragment>
      {/* consent cookie popup scripts */}
      <ConsentCookie />

      {/* react-helmet enables setting <head> contents, like title and OG meta tags */}
      <LayoutSeoTags route={route} />
      <JSONLDSeoTags route={route} />

      {/*
      VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
      If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
      For XM (CMS-only) apps, this should be removed.

      VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
    */}
      {/* <VisitorIdentification /> */}

      {matomoTagManagerScript && <RichText tag="div" field={{ value: matomoTagManagerScript }} editable={false} />}
      {aiScript && <RichText tag="div" field={{ value: aiScript }} editable={false} />}

      <header>
        <Placeholder name="jss-header" rendering={route} />
        <StickyTopWrapper />
      </header>
      <main>
        <Placeholder
          name="jss-main"
          rendering={route}
          render={(items) => {
            if (items[0]) {
              const firstItem = items[0];
              items[0] = React.cloneElement(firstItem, { isFirst: true });
            }
            return items;
          }}
        />
      </main>
      <div id="modal-EE-section" />
      <footer>
        <Placeholder name="jss-footer" rendering={route} />
      </footer>
    </React.Fragment>
  );
});

export default withSitecoreContext()(Layout);
